import {
  gameBoardSchema,
  gameResultSchema,
  gameSchema,
  type Game,
  type GameBoard,
  type GameResult,
} from '../games/game.js'
import type { BaseGear } from '../gear/gear.js'
import { gearSchema } from '../gear/gear.js'
import type {
  Agent,
  AgentGoal,
  AgentMemory,
  AgentProactivity,
  AgentReadCursors,
  AgentRole,
  AgentRuntimeStatus,
  AgentStatus,
} from './agent.js'
import {
  AGENT_TURN_MAX_SECONDS,
  agentProactivityLevels,
  agentReadCursorsSchema,
  agentRoles,
  agentRuntimeStatuses,
  agentSchema,
  agentStatuses,
  isAgent,
  roleForParent,
} from './agent.js'
import type { AudioClip, Clip, EntityClip } from './audio-clip.js'
import { audioClipSchema, isAudioClip, isEntityClip } from './audio-clip.js'
import type { AudioComment } from './audio-comment.js'
import { audioCommentSchema } from './audio-comment.js'
import type { Track } from './audio-track.js'
import { isAudioTrack, trackSchema } from './audio-track.js'
import type { BandSpace } from './band-space.js'
import { bandSpaceSchema, isBandSpace } from './band-space.js'
import type { Attendee, Calendar, CalendarEvent } from './calendar.js'
import { calendarEventSchema, calendarSchema, isCalendar, isCalendarEvent } from './calendar.js'
import type { Effect, EffectType } from './effect.js'
import { effectSchema, EffectTypeValues, isEffect } from './effect.js'
import type { Media } from './media.js'
import { isMedia, mediaSchema } from './media.js'
import type {
  Melody,
  MelodyEntityNote,
  MelodyFeel,
  MelodyGrid,
  MelodyInstrumentHint,
  MelodyMeter,
  MelodySource,
  MelodyTempo,
} from './melody.js'
import {
  isMelody,
  melodyEntityNoteSchema,
  melodyFeelSchema,
  melodyGrids,
  melodyGridSchema,
  melodyInstrumentHints,
  melodyInstrumentHintSchema,
  melodyMeterSchema,
  melodySchema,
  melodySourceKinds,
  melodySourceSchema,
  melodyTempoSchema,
} from './melody.js'
import type { Midi, MidiCC, MidiProgramChange } from './midi.js'
import { isMidi, isMidiCC, isMidiProgramChange, midiSchema } from './midi.js'
import type { MusicXML } from './musicxml.js'
import { isMusicXML, musicXMLSchema } from './musicxml.js'
import type { MusicReview, ReviewStatus, ReviewTrack } from './music-review.js'
import { isMusicReview, musicReviewSchema } from './music-review.js'
import type { Availability, ManyOfManyPollResponse, OneOfManyPollResponse, PollResponse } from './poll-response.js'
import {
  availabilityValues,
  isManyOfManyPollResponse,
  isOneOfManyPollResponse,
  isPollResponse,
  manyOfManyPollResponseSchema,
  oneOfManyPollResponseSchema,
  pollResponseSchema,
} from './poll-response.js'
import type { DateSlot, ManyOfManyPoll, OneOfManyPoll, Poll, PollOption, PollOptionAudio, PollType } from './poll.js'
import {
  isDatePoll,
  isDateSlot,
  isManyOfManyPoll,
  isOneOfManyPoll,
  isPoll,
  pollOptionAudioSchema,
  pollOptionSchema,
  pollSchema,
  pollTypes,
} from './poll.js'
import type { Rider, RiderHospitality, RiderMember, RiderMonitors, RiderShared, StageSize } from './rider.js'
import { isRider, riderSchema } from './rider.js'
import type { Setlist } from './setlist.js'
import { setlistSchema } from './setlist.js'
import type { Song } from './song.js'
import { isSong, songSchema } from './song.js'
import type { UserSettings, UserSettingsData } from './user-settings.js'
import { userSettingsSchema } from './user-settings.js'

const extensionSchemas = {
  /** we use non-passthrough for page for now, gradually moving the others over  */
  agent: agentSchema.loose(),
  audioclip: audioClipSchema.loose(),
  audiocomment: audioCommentSchema.loose(),
  audiotrack: trackSchema.loose(),
  song: songSchema.loose(),
  midi: midiSchema,
  melody: melodySchema.loose(),
  bandspace: bandSpaceSchema.loose(),
  calendar: calendarSchema.loose(),
  calendarevent: calendarEventSchema.loose(),
  musicxml: musicXMLSchema.loose(),
  usersettings: userSettingsSchema.loose(),
  effect: effectSchema,
  setlist: setlistSchema,
  pollresponse: pollResponseSchema,
  poll: pollSchema,
  rider: riderSchema.loose(),
  gear: gearSchema.loose(),
  game: gameSchema.loose(),
  gameresult: gameResultSchema.loose(),
  gameboard: gameBoardSchema.loose(),
  musicreview: musicReviewSchema.loose(),
}

export const extensionEntityNames = Object.keys(
  extensionSchemas
) as unknown as readonly (keyof typeof extensionSchemas)[]

export {
  AGENT_TURN_MAX_SECONDS,
  agentProactivityLevels,
  agentReadCursorsSchema,
  agentRoles,
  agentRuntimeStatuses,
  agentSchema,
  agentStatuses,
  audioClipSchema,
  audioCommentSchema,
  bandSpaceSchema,
  calendarEventSchema,
  calendarSchema,
  EffectTypeValues,
  extensionSchemas,
  gameBoardSchema,
  gameResultSchema,
  gameSchema,
  gearSchema,
  isAgent,
  roleForParent,
  isAudioClip,
  isAudioTrack,
  isBandSpace,
  isCalendar,
  isCalendarEvent,
  isEffect,
  isEntityClip,
  isManyOfManyPoll,
  isManyOfManyPollResponse,
  isMusicReview,
  musicReviewSchema,
  isMedia,
  isMelody,
  isMidi,
  isMidiCC,
  isMidiProgramChange,
  isMusicXML,
  isOneOfManyPoll,
  isOneOfManyPollResponse,
  availabilityValues,
  isDatePoll,
  isDateSlot,
  isPoll,
  isPollResponse,
  isRider,
  isSong,
  manyOfManyPollResponseSchema,
  mediaSchema,
  melodyEntityNoteSchema,
  melodyFeelSchema,
  melodyGrids,
  melodyGridSchema,
  melodyInstrumentHints,
  melodyInstrumentHintSchema,
  melodyMeterSchema,
  melodySchema,
  melodySourceKinds,
  melodySourceSchema,
  melodyTempoSchema,
  midiSchema,
  musicXMLSchema,
  oneOfManyPollResponseSchema,
  pollOptionAudioSchema,
  pollOptionSchema,
  pollResponseSchema,
  pollSchema,
  pollTypes,
  riderSchema,
  setlistSchema,
  songSchema,
  trackSchema,
  userSettingsSchema,
  type Agent,
  type AgentGoal,
  type AgentMemory,
  type AgentProactivity,
  type AgentReadCursors,
  type AgentRole,
  type AgentRuntimeStatus,
  type AgentStatus,
  type Attendee,
  type Availability,
  type AudioClip,
  type AudioComment,
  type BandSpace,
  type BaseGear,
  type Calendar,
  type CalendarEvent,
  type Clip,
  type DateSlot,
  type Effect,
  type EffectType,
  type EntityClip,
  type Game,
  type GameBoard,
  type GameResult,
  type ManyOfManyPoll,
  type ManyOfManyPollResponse,
  type Media,
  type Melody,
  type MelodyEntityNote,
  type MelodyFeel,
  type MelodyGrid,
  type MelodyInstrumentHint,
  type MelodyMeter,
  type MelodySource,
  type MelodyTempo,
  type Midi,
  type MidiCC,
  type MidiProgramChange,
  type MusicReview,
  type MusicXML,
  type ReviewStatus,
  type ReviewTrack,
  type OneOfManyPoll,
  type OneOfManyPollResponse,
  type Poll,
  type PollOption,
  type PollOptionAudio,
  type PollResponse,
  type PollType,
  type Rider,
  type RiderHospitality,
  type RiderMember,
  type RiderMonitors,
  type RiderShared,
  type Setlist,
  type StageSize,
  type Song,
  type Track,
  type UserSettings,
  type UserSettingsData,
}
